import {
  DATE_PIPE_DEFAULT_OPTIONS,
  DatePipeConfig,
  provideImageKitLoader,
  registerLocaleData,
} from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeMx from '@angular/common/locales/es-MX';
import {
  APP_ID,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  isDevMode,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideNgIconsConfig } from '@ng-icons/core';
import {
  ENVIRONMENT,
  provideScannerConfig,
  provideScannerWsUrl,
} from '@seatix/check-in-config';
import {
  provideGateValidationBypass,
  provideRoleBypassChecker,
} from '@seatix/check-in-data-access-offline-sync';
import {
  provideCheckInGatesInitializer,
  unauthenticatedInterceptor,
} from '@seatix/check-in-data-access-scanner';
import { providePwaUpdate } from '@seatix/check-in-util-pwa-update';
import {
  provideApiUrl,
  provideCdnUrl,
  provideScanApiUrl,
} from '@seatix/shared-config';
import { provideAlertToast } from '@seatix/shared-util-alert';
import { provideAppVersion } from '@seatix/shared-util-app-version';
import { provideAppVersionRefreshHandler } from '@seatix/shared-util-app-version-refresh-handler';
import { provideThemeInitializer } from '@seatix/shared-util-theme-switcher';
import {
  ensureCsrfTokenInterceptor,
  errorNormalizationInterceptor,
  headersInterceptor,
  retryInterceptor,
} from '@seatix/shared/core';
import {
  AUTH_PROVIDER,
  AuthApiService,
  authTokenInterceptor,
  provideTokenInitializer,
  TokenAuthProvider,
} from '@seatix/shared/data-access/auth';
import {
  provideVersionInterceptorConfig,
  versionInterceptor,
} from '@seatix/util-http-version-interceptor';
import { appRoutes } from './app.routes';

registerLocaleData(localeMx, 'es-MX');

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_ID,
      useValue: 'check-in',
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
      },
    },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(appRoutes),
    provideHttpClient(
      withInterceptors([
        retryInterceptor,
        ensureCsrfTokenInterceptor,
        authTokenInterceptor,
        versionInterceptor,
        headersInterceptor,
        errorNormalizationInterceptor,
        unauthenticatedInterceptor,
      ]),
    ),
    provideVersionInterceptorConfig(),
    provideApiUrl(process.env['STX_API_URL'] || '/api'),
    provideScanApiUrl(process.env['STX_SCAN_API_URL'] || '/scan_backend'),
    provideCdnUrl(process.env['STX_CDN_URL'] || '/cdn'),
    {
      provide: ENVIRONMENT,
      useValue:
        (process.env['STX_ENVIRONMENT'] as
          | 'development'
          | 'staging'
          | 'production') || 'development',
    },
    provideScannerConfig(),
    provideThemeInitializer(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:5000',
    }),
    provideAppVersionRefreshHandler(),
    providePwaUpdate(),
    provideAlertToast(),
    provideNgIconsConfig({
      size: '24px',
    }),
    provideImageKitLoader('https://ik.imagekit.io/seatix'),
    provideAppVersion(process.env['STX_APP_VERSION'] || 'dev'),
    AuthApiService,
    {
      provide: AUTH_PROVIDER,
      useClass: TokenAuthProvider,
    },
    provideTokenInitializer({ timeoutMs: 5000 }),
    provideCheckInGatesInitializer({ timeoutMs: 5000 }),
    provideGateValidationBypass(['owner', 'manager']),
    provideRoleBypassChecker(),
    {
      provide: LOCALE_ID,
      useValue: 'es-MX',
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'MXN',
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
      },
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useFactory: (): DatePipeConfig => ({
        dateFormat: "EEEE, dd 'de' MMMM 'de' yyyy, hh:mm a",
      }),
    },
    provideScannerWsUrl(
      process.env['STX_SCANNER_WS_URL'] || '/ws',
    ),
  ],
};
